import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Vector3 } from "three";
import { useFrame } from "@react-three/fiber";
import { useAnimationsController } from "../Animation/AnimationsController";

const REGISTERED_PLAYER_MAP: Record<string, boolean> = {};

const rumble = (gp: Gamepad | null = null) => {
  try {
    if (gp?.vibrationActuator) {
      gp.vibrationActuator
        .playEffect("dual-rumble", {
          startDelay: 200,
          duration: 25,
          weakMagnitude: 0.5,
          strongMagnitude: 0.5,
        })
        .then(() => undefined);
    } else {
      navigator.vibrate([0, 200, 25]);
    }
  } catch (error) {
    // Ignore.
  }
};

export const WALKING_SPEED = 0.75;
export const RUNNING_SPEED_MULTIPLIER = 1.5;
export const RUNNING_SPEED = WALKING_SPEED * RUNNING_SPEED_MULTIPLIER;
export const JUMP_SPEED = RUNNING_SPEED_MULTIPLIER * 0.75;
export const RUNNING_JUMP_SPEED = JUMP_SPEED / 1.25;
export const FALLING_SPEED_MULTIPLIER = 0.25;

export type PlayerState = {
  direction: Vector3;
  mirrored: boolean;
  idle: boolean;
  running: boolean;
  jumping: boolean;
  falling: boolean;
  airControlEnabled: () => boolean;
};

export const getSpeed = ({
  running,
}: PlayerState): {
  speed: number;
  jumpSpeed: number;
} => {
  const speed = running ? RUNNING_SPEED : WALKING_SPEED;
  const jumpSpeed = running ? RUNNING_JUMP_SPEED : JUMP_SPEED;

  return {
    speed,
    jumpSpeed,
  };
};

export const getMoveDirectionWithSpeed = (
  direction: Vector3,
  speed: number,
  useZ: boolean = false
): Vector3 => {
  const { x, z } = direction;
  const angle = Math.atan2(Math.abs(z), Math.abs(x));
  const newXSpeed = Math.cos(angle) * speed;
  const newZSpeed = Math.sin(angle) * speed;

  return useZ
    ? new Vector3(direction.x, direction.y, direction.z * newZSpeed)
    : new Vector3(direction.x * newXSpeed, direction.y, direction.z);
};

export enum PlayerAnimationNames {
  Idle = "Idle",
  Walking = "Walking",
  Running = "Running",
  Jumping = "Jumping",
}

export enum PlayerAnimations {
  Idle = "Idle",
  Walking = "Walking",
  Running = "Running",
  Jumping = "Jumping",
}

export enum PlayerMirroredAnimations {
  Idle = "IdleMirror",
  Walking = "WalkingMirror",
  Running = "RunningMirror",
  Jumping = "JumpingMirror",
}

export const AllPlayerAnimations = [
  ...Object.values(PlayerAnimations),
  ...Object.values(PlayerMirroredAnimations),
];

export type PlayerKeyboardMapping = {
  Jump: string;
  Forward: string;
  Left: string;
  Backward: string;
  Right: string;
  Run: string;
};

export const DEFAULT_PLAYER_KEYBOARD_MAPPING: PlayerKeyboardMapping = {
  Jump: " ",
  Forward: "w",
  Left: "a",
  Backward: "s",
  Right: "d",
  Run: "shift",
};

export type PlayerControls = {
  playerStateRef: MutableRefObject<PlayerState>;
  onFallStart: () => void;
  onFallEnd: () => void;
};

export const usePlayerControls = (
  active: boolean = false,
  playerKeyboardMapping: PlayerKeyboardMapping = DEFAULT_PLAYER_KEYBOARD_MAPPING
): PlayerControls => {
  const {
    Jump: jumpKey,
    Forward: forwardKey,
    Left: leftKey,
    Backward: backwardKey,
    Right: rightKey,
    Run: runKey,
  } = playerKeyboardMapping;
  const { stopAnimations, playAnimations } = useAnimationsController();
  const [gamepadListLength, setGamepadListLength] = useState<number>(
    navigator.getGamepads().length
  );
  const gamepadIndexRef = useRef<number>(-1);
  const getGamepad = useCallback(
    () => navigator.getGamepads()[gamepadIndexRef.current],
    []
  );
  const fallingTimestampRef = useRef<number>(0);
  const airControlEnabled = useCallback(() => {
    return Date.now() - fallingTimestampRef.current < 1000;
  }, []);
  const playerStateRef = useRef<PlayerState>({
    direction: new Vector3(),
    mirrored: false,
    idle: true,
    running: false,
    jumping: false,
    falling: false,
    airControlEnabled,
  });
  const onUpdatePlayerState = useCallback(
    (newStateProps: Partial<PlayerState>) => {
      const preNewState = {
        ...playerStateRef.current,
        ...newStateProps,
      };
      const { mirrored: wasMirrored = false } = playerStateRef.current;
      const mirrored =
        preNewState.direction.x < 0
          ? false
          : preNewState.direction.x > 0
          ? true
          : wasMirrored;
      const idle = preNewState.direction.toArray().every((v) => v === 0);

      playerStateRef.current = {
        ...preNewState,
        mirrored,
        idle,
      };

      const { running, jumping, falling } = playerStateRef.current;
      const animKeyName = jumping
        ? PlayerAnimationNames.Jumping
        : falling
        ? PlayerAnimationNames.Jumping
        : running && !idle
        ? PlayerAnimationNames.Running
        : idle
        ? PlayerAnimationNames.Idle
        : PlayerAnimationNames.Walking;
      const animMap = mirrored ? PlayerMirroredAnimations : PlayerAnimations;
      const anim = animMap[animKeyName];

      stopAnimations(AllPlayerAnimations);
      playAnimations([anim]);
    },
    [stopAnimations, playAnimations]
  );
  const getCurrentSpeed = useCallback(
    () => getSpeed(playerStateRef.current),
    []
  );
  const canJump = useCallback(
    () => !(playerStateRef.current.jumping || playerStateRef.current.falling),
    []
  );
  const onFallStart = useCallback(() => {
    fallingTimestampRef.current = Date.now();

    onUpdatePlayerState({
      falling: true,
    });
  }, [onUpdatePlayerState]);
  const onFallEnd = useCallback(() => {
    // GP Vibes!
    rumble(getGamepad());

    onUpdatePlayerState({
      direction: new Vector3(
        playerStateRef.current.direction.x,
        0,
        playerStateRef.current.direction.z
      ),
      jumping: false,
      falling: false,
    });
  }, [onUpdatePlayerState, getGamepad]);
  const onSetRunning = useCallback(
    (running: Boolean = true) =>
      onUpdatePlayerState({
        // @ts-ignore
        running,
      }),
    [onUpdatePlayerState]
  );
  const onMoveX = useCallback(
    (speedMultiplier: number) => {
      const { speed } = getCurrentSpeed();

      onUpdatePlayerState({
        direction: getMoveDirectionWithSpeed(
          new Vector3(
            speedMultiplier,
            playerStateRef.current.direction.y,
            playerStateRef.current.direction.z
          ),
          speed,
          false
        ),
      });
    },
    [onUpdatePlayerState]
  );
  const onMoveZ = useCallback(
    (speedMultiplier: number) => {
      const { speed } = getCurrentSpeed();

      onUpdatePlayerState({
        direction: getMoveDirectionWithSpeed(
          new Vector3(
            playerStateRef.current.direction.x,
            playerStateRef.current.direction.y,
            speedMultiplier
          ),
          speed,
          true
        ),
      });
    },
    [onUpdatePlayerState]
  );
  const onJump = useCallback(
    (speedMultiplier: number = 1) => {
      if (canJump()) {
        const { jumpSpeed } = getCurrentSpeed();

        onUpdatePlayerState({
          direction: new Vector3(
            playerStateRef.current.direction.x,
            jumpSpeed * speedMultiplier,
            playerStateRef.current.direction.z
          ),
          jumping: true,
        });

        setTimeout(() => {
          onUpdatePlayerState({
            direction: new Vector3(
              playerStateRef.current.direction.x,
              0,
              playerStateRef.current.direction.z
            ),
            jumping: false,
          });
        }, 300);
      }
    },
    [onUpdatePlayerState, canJump]
  );
  const playerKeyMap: {
    keyDown: Record<string, () => void>;
    keyUp: Record<string, () => void>;
  } = useMemo(
    () => ({
      keyDown: {
        [jumpKey]: () => onJump(1),
        [forwardKey]: () => onMoveZ(-1),
        [leftKey]: () => onMoveX(-1),
        [backwardKey]: () => onMoveZ(1),
        [rightKey]: () => onMoveX(1),
        [runKey]: () => onSetRunning(true),
      },
      keyUp: {
        [jumpKey]: () => undefined,
        [forwardKey]: () => onMoveZ(0),
        [leftKey]: () => onMoveX(0),
        [backwardKey]: () => onMoveZ(0),
        [rightKey]: () => onMoveX(0),
        [runKey]: () => onSetRunning(false),
      },
    }),
    [onJump, onMoveX, onMoveZ]
  );
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!getGamepad()) {
        const handler = playerKeyMap.keyDown[e.key.toLowerCase()];

        if (handler) {
          handler();
        }
      }
    },
    [playerKeyMap, getGamepad]
  );
  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (!getGamepad()) {
        const handler = playerKeyMap.keyUp[e.key.toLowerCase()];

        if (handler) {
          handler();
        }
      }
    },
    [playerKeyMap, getGamepad]
  );

  useFrame(() => {
    if (active) {
      const gpListLen = navigator.getGamepads().length;
      const gp = getGamepad();

      if (gpListLen !== gamepadListLength) {
        setGamepadListLength(gpListLen);
      }

      if (gp) {
        // Run
        if (gp.buttons[2].pressed) {
          onSetRunning(true);
        } else if (playerStateRef.current.running) {
          onSetRunning(false);
        }

        // Left/Right
        if (gp.axes[0] !== 0) {
          onMoveX(gp.axes[0]);
        } else {
          onMoveX(0);
        }

        // Forward/Backward
        if (gp.axes[1] !== 0) {
          onMoveZ(gp.axes[1]);
        } else {
          onMoveZ(0);
        }

        // Jump
        if (gp.buttons[0].pressed) {
          onJump(gp.buttons[0].value);
        }
      }
    }
  });

  useEffect(() => {
    if (active) {
      const gpList = navigator.getGamepads();

      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);

      stopAnimations(AllPlayerAnimations);
      onUpdatePlayerState(playerStateRef.current);

      if (gamepadIndexRef.current === -1) {
        for (let i = 0; i < gpList.length; i++) {
          if (!REGISTERED_PLAYER_MAP[i]) {
            REGISTERED_PLAYER_MAP[i] = true;

            gamepadIndexRef.current = i;
            break;
          }
        }
      }

      return () => {
        const gamepadIndex = gamepadIndexRef.current;

        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);

        if (gamepadIndex > -1) {
          delete REGISTERED_PLAYER_MAP[gamepadIndex];
        }
      };
    }
  }, [active, stopAnimations, onUpdatePlayerState, gamepadListLength]);

  return {
    playerStateRef,
    onFallStart,
    onFallEnd,
  };
};
